// ルール解説ページ（/rules/）のシェル用スクリプト。盤（ChessGame）が無いので main.js は読まず、
// ページ共通の最低限（言語切替・☰の受け皿・計測/広告・エラー収集）と、ミニ盤 JS の遅延読込だけを持つ。
(function () {
    'use strict';

    // ---- 早期エラー収集（main.js と同じ契約。feedback.js が読み込まれた時点で drain する） ----
    const MAX_EARLY_ERRORS = 3;
    const MAX_STACK_FRAMES = 2;
    window.__chessEarlyErrors = [];

    function recordEarlyError(source, message) {
        if (!message || !window.__chessEarlyErrors) return;
        window.__chessEarlyErrors.push({ source, message: String(message).slice(0, 300), at: Date.now() });
        if (window.__chessEarlyErrors.length > MAX_EARLY_ERRORS) window.__chessEarlyErrors.shift();
    }
    function stackHead(error) {
        const stack = typeof error?.stack === 'string' ? error.stack : '';
        if (!stack) return '';
        const frames = stack.split('\n')
            .map(line => line.trim())
            .filter(line => line.startsWith('at ') || line.includes('@'))
            .slice(0, MAX_STACK_FRAMES)
            .map(line => line.replace(/https?:\/\/[^/]+/g, ''));
        return frames.length ? ` | ${frames.join(' < ')}` : '';
    }
    window.addEventListener('error', e => {
        const where = e.filename ? ` @${String(e.filename).split('/').pop()}:${e.lineno}:${e.colno || 0}` : '';
        recordEarlyError('error', (e.message || 'error') + where + stackHead(e.error));
    });
    window.addEventListener('unhandledrejection', e => {
        recordEarlyError('promise', (e.reason?.message || e.reason || 'unhandledrejection') + stackHead(e.reason));
    });

    // ---- 遅延読込の道具（main.js と同じ） ----
    function whenIdle(task, timeout = 2000) {
        if (typeof requestIdleCallback === 'function') requestIdleCallback(task, timeout ? { timeout } : undefined);
        else setTimeout(task, 0);
    }
    const loadedScripts = new Map();
    function loadScriptOnce(src) {
        if (!loadedScripts.has(src)) {
            loadedScripts.set(src, new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = src;
                script.onload = () => resolve();
                script.onerror = () => {
                    loadedScripts.delete(src); // 次の機会に読み直せるようにする
                    reject(new Error(`Failed to load ${src}`));
                };
                document.head.appendChild(script);
            }));
        }
        return loadedScripts.get(src);
    }
    const FEEDBACK_SCRIPT_SRC = '/feedback.js';
    const BOARD_SCRIPT_SRC = '/rules-board.js';

    // 言語メニューを閉じる処理。main.js の setupLanguageMenu と同じ（理由のコメントもそちら）
    function setupLanguageMenu() {
        const menu = document.getElementById('lang-menu');
        if (!menu) return;
        const close = () => { menu.open = false; };
        document.addEventListener('pointerdown', (e) => {
            if (menu.open && !menu.contains(e.target)) close();
        });
        menu.addEventListener('focusout', (e) => {
            if (menu.open && e.relatedTarget && !menu.contains(e.relatedTarget)) close();
        });
        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape' || !menu.open) return;
            e.stopPropagation();
            close();
            menu.querySelector('summary').focus();
        }, true);
        window.addEventListener('pagehide', close);
    }

    // フィードバックに添える盤の状況。このページに盤は無いのでモード名だけ
    window.chessFeedbackContext = () => ({ mode: 'rules' });

    // feedback.js が届く前に ☰ を押されたら、その場で読み込んで開く
    let feedbackLoadPending = false;
    function setupDeferredFeedback() {
        const menuButton = document.getElementById('menu-button');
        if (!menuButton) return;
        const onEarlyClick = () => {
            if (window.chessFeedbackReady) {
                menuButton.removeEventListener('click', onEarlyClick);
                return;
            }
            if (feedbackLoadPending) return;
            feedbackLoadPending = true;
            loadScriptOnce(FEEDBACK_SCRIPT_SRC).then(() => {
                menuButton.removeEventListener('click', onEarlyClick);
                if (window.chessFeedbackReady) menuButton.click();
            }).catch(() => {}).finally(() => { feedbackLoadPending = false; });
        };
        menuButton.addEventListener('click', onEarlyClick);
    }

    // ---- ミニ盤の JS は、盤が画面に近づいてから読む（開いた瞬間には読まない） ----
    let boardLoad = null;
    function loadBoardScript() {
        if (!boardLoad) {
            boardLoad = loadScriptOnce(BOARD_SCRIPT_SRC).catch(err => { boardLoad = null; throw err; });
        }
        return boardLoad;
    }
    function setupDeferredBoards() {
        const widgets = Array.from(document.querySelectorAll('.rules-widget[data-widget]'));
        if (!widgets.length) return;
        // 読み込み前に押されたボタンは、到着後にもう一度押したことにする（☰ と同じ受け皿）
        for (const widget of widgets) {
            const onEarlyClick = (e) => {
                if (window.chessRulesBoardReady) {
                    widget.removeEventListener('click', onEarlyClick);
                    return;
                }
                const button = e.target.closest('button');
                if (!button) return;
                loadBoardScript().then(() => {
                    widget.removeEventListener('click', onEarlyClick);
                    if (window.chessRulesBoardReady) button.click();
                }).catch(() => {});
            };
            widget.addEventListener('click', onEarlyClick);
        }
        if (typeof IntersectionObserver !== 'function') {
            window.addEventListener('load', () => whenIdle(() => loadBoardScript().catch(() => {})));
            return;
        }
        const observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return;
            observer.disconnect();
            loadBoardScript().catch(() => {});
        }, { rootMargin: '300px 0px' });
        widgets.forEach(widget => observer.observe(widget));
    }

    window.addEventListener('load', function () {
        var script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=G-2T79P38VJ2';
        document.head.appendChild(script);

        script.onload = function () {
            window.dataLayer = window.dataLayer || [];
            function gtag() { dataLayer.push(arguments); }
            gtag('js', new Date());
            gtag('config', 'G-2T79P38VJ2');
        };

        var adsScript = document.createElement('script');
        adsScript.async = true;
        adsScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1918692579240633';
        adsScript.crossOrigin = 'anonymous';
        document.head.appendChild(adsScript);

        // 初期表示が終わってから読み込むもの。失敗しても ☰ を押した時点で読み直せる
        whenIdle(() => { loadScriptOnce(FEEDBACK_SCRIPT_SRC).catch(() => {}); });
    });

    function init() {
        setupLanguageMenu();
        setupDeferredFeedback();
        setupDeferredBoards();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
